interface SavedLocation {
  location: string;           // User input text
  submittedLocation: string;  // API display name
  lat: number;
  lng: number;
  timestamp: number;          // When saved
  expiresAt: number;         // 7 days from save
}

const STORAGE_KEY = 'sunrise-sunset-saved-location';
const EXPIRATION_DAYS = 7;

/**
 * Save a location to localStorage with 7-day expiration
 */
export const saveLocation = (
  location: string,
  submittedLocation: string,
  lat: number,
  lng: number
): void => {
  try {
    const now = Date.now();
    const expiresAt = now + (EXPIRATION_DAYS * 24 * 60 * 60 * 1000); // 7 days in milliseconds
    
    const savedLocation: SavedLocation = {
      location,
      submittedLocation,
      lat,
      lng,
      timestamp: now,
      expiresAt
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedLocation));
  } catch (error) {
    console.warn('Failed to save location to localStorage:', error);
  }
};

/**
 * Restore a saved location from localStorage if it exists and hasn't expired
 */
export const restoreLocation = (): SavedLocation | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return null;
    }
    
    const savedLocation: SavedLocation = JSON.parse(saved);
    const now = Date.now();
    
    // Check if the saved location has expired
    if (now > savedLocation.expiresAt) {
      // Clean up expired entry
      clearSavedLocation();
      return null;
    }
    
    return savedLocation;
  } catch (error) {
    console.warn('Failed to restore location from localStorage:', error);
    // Clean up corrupted data
    clearSavedLocation();
    return null;
  }
};

/**
 * Clear the saved location from localStorage
 */
export const clearSavedLocation = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear saved location from localStorage:', error);
  }
};

/**
 * Check if there's a valid saved location without retrieving it
 */
export const hasSavedLocation = (): boolean => {
  return restoreLocation() !== null;
};

export type { SavedLocation };
